import { z } from 'zod';
import { AdsFunnelSettingValue, AdsFunnelSettingValueOut, AdsFunnelSettingValueStrict, } from './ads-builder.js';
import { AdsPaybackDay } from './ads-payback.js';
/*
 * Правка настроек воронки живой кампании и статистика по версиям настроек (S3,
 * `docs/live-edit-2026-11-04/spec-s3-funnel-settings-revisions.md` §10.3, §11).
 *
 * В интерфейсе слово «версия» (в коде и API — `revision`). Деньги — центы (`*Minor`).
 * Разбор ответов мягкий: auto-ads пишется параллельно, а часть полей включается флагом
 * (`CAMPAIGN_SETTINGS_ANALYTICS_ENABLED`) — нет поля → `null` / пусто, экран прячет блок.
 */
export const ADS_FUNNEL_REVISION_REASON_MAX = 500;
export const ADS_FUNNEL_REVISION_MAX_KEYS = 50;
const Iso = z.string().refine((v) => !Number.isNaN(Date.parse(v)), { message: 'Неверная дата' });
const nn = z.number().nullable();
const soft = (s) => s
    .nullish()
    .catch(null)
    .transform((v) => (v === undefined ? null : v));
const softNum = soft(z.number());
const softStr = soft(z.string());
const softBool = z
    .boolean()
    .nullish()
    .catch(null)
    .transform((v) => v === true);
// ---------- запись версии ----------
/**
 * Тело `POST /funnel-settings/:settingsId/revisions` (зеркало маршрута auto-ads, §4.2): ровно одно из
 * `values` (полный новый набор) / `revertToRevision`; причина обязательна; `clientRequestId` (uuid)
 * обязателен, если это не просмотр (`dryRun`) — экран создаёт его при открытии диалога.
 */
export const AdsFunnelRevisionWriteBody = z
    .object({
    expectedRevision: z.number().int().min(1),
    values: z
        .record(z.string().min(1).max(100), AdsFunnelSettingValue)
        .refine((v) => Object.keys(v).length <= ADS_FUNNEL_REVISION_MAX_KEYS, {
        message: `Не больше ${ADS_FUNNEL_REVISION_MAX_KEYS} настроек`,
    })
        .optional(),
    revertToRevision: z.number().int().min(1).optional(),
    reason: z
        .string()
        .trim()
        .min(1, { message: 'Укажите причину изменения' })
        .max(ADS_FUNNEL_REVISION_REASON_MAX, {
        message: `Причина — не длиннее ${ADS_FUNNEL_REVISION_REASON_MAX} символов`,
    }),
    clientRequestId: z.string().uuid().optional(),
    dryRun: z.boolean().optional(),
})
    .strict()
    .superRefine((b, ctx) => {
    if ((b.values === undefined) === (b.revertToRevision === undefined)) {
        ctx.addIssue({
            code: 'custom',
            message: 'Нужно ровно одно: новые значения или версия для возврата',
        });
    }
    if (b.dryRun !== true && b.clientRequestId === undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['clientRequestId'],
            message: 'Нужен идентификатор запроса (clientRequestId)',
        });
    }
});
/** Строка «было → стало» (сырые значения; слова подставляет экран по каталогу). */
export const AdsFunnelRevisionDiffRow = z.object({
    key: z.string(),
    before: AdsFunnelSettingValueOut.nullish().transform((v) => v ?? null),
    after: AdsFunnelSettingValueOut.nullish().transform((v) => v ?? null),
});
/**
 * Ответ записи / просмотра. Просмотр (`dryRun: true`): `wouldWriteRevision`, `minNextAllowedAt`,
 * `catalogVersion`, `stale`. Запись (201 / 200 при повторе): `revision`, `revisionId`,
 * `effectiveFrom`, `kind`, `cacheNote`, `idempotentReplay`. Общие: `diff`, `changedKeys`.
 */
export const AdsFunnelRevisionWriteResult = z.object({
    dryRun: softBool,
    settingsId: softStr,
    revisionId: softStr,
    revision: softNum,
    wouldWriteRevision: softNum,
    effectiveFrom: soft(Iso),
    minNextAllowedAt: soft(Iso),
    catalogVersion: softStr,
    stale: softBool,
    kind: softStr,
    cacheNote: softStr,
    idempotentReplay: softBool,
    changedKeys: z
        .array(z.string())
        .nullish()
        .catch(null)
        .transform((v) => v ?? []),
    diff: z
        .array(AdsFunnelRevisionDiffRow)
        .nullish()
        .catch(null)
        .transform((v) => v ?? []),
    /** Предупреждения для экрана строит сам экран; сервер их не шлёт (поле для будущего). */
    warnings: z
        .array(z.string())
        .nullish()
        .catch(null)
        .transform((v) => v ?? []),
});
// ---------- статистика по версиям ----------
export const AdsCampaignRevisionStatsQuery = z
    .object({
    campaignId: z.string().regex(/^\d{6,25}$/, { message: 'Неверный id кампании' }),
    platformAccountId: z.string().regex(/^[a-f0-9]{24}$/i, { message: 'Неверный id кабинета' }),
    from: AdsPaybackDay,
    to: AdsPaybackDay,
    /** `1` — добавить блок окупаемости к каждой версии (по умолчанию да). */
    includePayback: z.enum(['0', '1']).optional(),
})
    .refine((q) => q.from <= q.to, { message: 'Начало периода позже конца', path: ['from'] })
    .refine((q) => Math.round((Date.parse(`${q.to}T00:00:00Z`) - Date.parse(`${q.from}T00:00:00Z`)) / 86_400_000) +
    1 <=
    92, { message: 'Период длиннее 92 дней', path: ['to'] });
export const ADS_REVISION_FUNNEL_UNAVAILABLE = [
    'funnel_disabled',
    'funnel_timeout',
    'funnel_error',
    'funnel_busy',
    'live_window_limit',
];
const FunnelUnavailable = z
    .string()
    .nullish()
    .catch(null)
    .transform((v) => v ?? null);
const RevisionSpend = z.object({
    spendMinor: z.number(),
    impressions: z.number().catch(0),
    clicks: z.number().catch(0),
    basis: softStr,
    estimated: softBool,
    preliminary: softBool,
    mixedDays: z
        .array(z.string())
        .nullish()
        .catch(null)
        .transform((v) => v ?? []),
});
const RevisionPurchases = z.object({
    count: z.number(),
    firstChargeMinor: z.number().catch(0),
    byRevisionSource: z
        .object({ ref: z.number().catch(0), window: z.number().catch(0) })
        .nullish()
        .catch(null)
        .transform((v) => v ?? { ref: 0, window: 0 }),
});
const RevisionFunnel = z
    .object({
    visits: z.number().catch(0),
    quizStarted: z.number().catch(0),
    emails: z.number().catch(0),
    checkouts: z.number().catch(0),
    trials: z.number().catch(0),
    visitToTrial: nn.catch(null),
})
    .nullish()
    .catch(null)
    .transform((v) => v ?? null);
const RevisionPayback = z
    .object({
    roas60: nn.catch(null),
    roas90: nn.catch(null),
    roas120: nn.catch(null),
    verdict: z.enum(['yes', 'unclear', 'no']).nullable().catch(null),
    coverage: nn.catch(null),
    spendMinorFull: z.number().catch(0),
    purchases: z.number().catch(0),
    purchasesWithPrediction: z.number().catch(0),
})
    .nullish()
    .catch(null)
    .transform((v) => v ?? null);
export const AdsCampaignRevisionRow = z.object({
    revision: z.number().int(),
    revisionId: softStr,
    effectiveFrom: Iso,
    effectiveTo: soft(Iso),
    source: z
        .object({ kind: z.string().catch('manual'), via: softStr })
        .nullish()
        .catch(null)
        .transform((v) => v ?? { kind: 'manual', via: null }),
    reason: softStr,
    createdBy: softStr,
    changedKeys: z
        .array(z.string())
        .nullish()
        .catch(null)
        .transform((v) => v ?? []),
    revertOf: softStr,
    /** Окно версии ∩ период (дни кабинета); `null` — окно вне периода (строка из-за покупок по ссылке). */
    clip: z
        .object({ from: z.string(), to: z.string() })
        .nullish()
        .catch(null)
        .transform((v) => v ?? null),
    spend: RevisionSpend,
    purchases: RevisionPurchases,
    /** Расход ÷ число покупок версии (НЕ «цена покупки» окупаемости). */
    cppMinor: nn.catch(null),
    funnel: RevisionFunnel,
    funnelUnavailable: FunnelUnavailable,
    payback: RevisionPayback,
});
export const AdsCampaignRevisionStats = z.object({
    campaignId: z.string(),
    settingsId: softStr,
    state: z.enum(['active', 'tombstoned']).nullable().catch(null),
    from: z.string().optional(),
    to: z.string().optional(),
    asOf: softStr,
    timezone: softStr,
    /** `no-funnel-settings` — у кампании нет настроек воронки. */
    note: softStr,
    // одна битая строка не обнуляет весь список: строка, не прошедшая схему, отбрасывается
    revisions: z
        .array(AdsCampaignRevisionRow.nullish().catch(null))
        .nullish()
        .catch(null)
        .transform((v) => (v ?? []).filter((r) => r !== null && r !== undefined)),
    outsideRevisions: z
        .object({ spendMinor: z.number().catch(0), note: softStr })
        .nullish()
        .catch(null)
        .transform((v) => v ?? { spendMinor: 0, note: null }),
    totals: z
        .object({
        spendMinor: z.number().catch(0),
        purchases: z.number().catch(0),
        cppMinor: nn.catch(null),
    })
        .nullish()
        .catch(null)
        .transform((v) => v ?? { spendMinor: 0, purchases: 0, cppMinor: null }),
    reconciliation: z
        .object({
        spendEqualsDaily: softBool,
        purchasesEqualsEvents: softBool,
        /** Сколько дней расхода посчитано каждым способом (часы / по дню / равномерно). */
        basisCounts: z.record(z.string(), z.number()).nullish().catch(undefined),
    })
        .nullish()
        .catch(null)
        .transform((v) => v ?? { spendEqualsDaily: false, purchasesEqualsEvents: false }),
    funnelWindow: z
        .object({ from: z.string(), to: z.string() })
        .nullish()
        .catch(null)
        .transform((v) => v ?? null),
    funnelUnavailable: FunnelUnavailable,
});
// ---------- вход агента (MCP `launch_funnel_settings_change`) ----------
/**
 * Вход инструмента MCP `launch_funnel_settings_change` (S3 §10.2), строго: лишнее поле или значение
 * чужой формы — понятная ошибка. Живёт здесь, а не в mcp-api: вход инструмента строится той же сборкой
 * zod, что и контракты (см. `contract-bridge.ts` mcp-api).
 */
export const AdsFunnelSettingsChangeAgentInput = z
    .strictObject({
    campaignId: z.string().regex(/^\d{6,25}$/, { message: 'Meta campaign id: 6-25 digits' }),
    expectedRevision: z.number().int().min(1),
    set: z.record(z.string().min(1).max(100), AdsFunnelSettingValueStrict).optional(),
    remove: z.array(z.string().min(1).max(100)).max(ADS_FUNNEL_REVISION_MAX_KEYS).optional(),
    revertToRevision: z.number().int().min(1).optional(),
    reason: z.string().trim().min(1).max(ADS_FUNNEL_REVISION_REASON_MAX),
    apply: z.boolean().optional(),
})
    .refine((a) => a.revertToRevision !== undefined
    ? a.set === undefined && a.remove === undefined
    : Object.keys(a.set ?? {}).length + (a.remove?.length ?? 0) > 0, { message: 'give either set / remove (at least one key) or revertToRevision, not both' });
//# sourceMappingURL=ads-funnel-settings.js.map